<template>
  <div :class="$style.myGamesCategory">
    <div :class="$style.myGamesCategoryTabsHolder">
      <BitMenuScrollable
        :class="$style.myGamesCategoryTabs"
        :list-class="$style.myGamesCategoryList"
        list-max-width="1192px"
      >
        <BitTabButton
          color="transparent"
          :active="activeTab === 'favorites'"
          :class="$style.myGamesTabButton"
          is-with-external-icon
          @click="setActiveTab('favorites')"
        >
          <img
            :src="storeCataloguePage.favoriteIcon.url"
            width="20"
            height="20"
            :class="$style.tabIcon"
            alt=""
          />
          <span>{{ storeCataloguePage.favoriteTitle }}</span>
        </BitTabButton>
        <BitTabButton
          color="transparent"
          :active="activeTab === 'recent'"
          is-with-external-icon
          :class="$style.myGamesTabButton"
          @click="setActiveTab('recent')"
        >
          <img
            :src="storeCataloguePage.lastPlayedIcon.url"
            width="20"
            height="20"
            :class="$style.tabIcon"
            alt=""
          />
          <span>{{ storeCataloguePage.lastPlayedTitle }}</span>
        </BitTabButton>
      </BitMenuScrollable>
    </div>
    <MainPageCatalogueContainer
      v-if="activeTab === 'favorites'"
      :class="classForContainer"
      :games-list="favoriteGames"
      :gtm-action="gtmAction"
      :favorite-games="favoriteGamesIds"
      :is-loading="isGamesLoading"
      @addToFavorite="$emit('addToFavorite', $event)"
      @removeFromFavorite="$emit('removeFromFavorite', $event)"
      @setCountCatalogueItem="$emit('setCountCatalogueItem', $event)"
    />
    <MainPageCatalogueContainer
      v-else
      :class="classForContainer"
      :games-list="recentGames"
      :gtm-action="gtmAction"
      :favorite-games="favoriteGamesIds"
      :is-loading="isGamesLoading"
      @addToFavorite="$emit('addToFavorite', $event)"
      @removeFromFavorite="$emit('removeFromFavorite', $event)"
      @setCountCatalogueItem="$emit('setCountCatalogueItem', $event)"
    />
    <div v-if="!isGamesLoading && !isActiveTabHasGames">
      <p :class="$style.gamesCatalogueNoGames">
        {{ storeCataloguePage.noGamesTitle }}
      </p>
      <CatalogueRow
        :collection="suggestionCollection"
        :games-list="suggestionGames"
        :show-all-title="storeCataloguePage.showAllGamesTitle"
        :gtm-action="gtmAction"
        :header-text="storeCataloguePage.noGamesButWeRecommendForYou"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BitTabButton, BitMenuScrollable } from '@bit/ui-kit';
import MainPageCatalogueContainer from '~/components/mainPage/catalogueContainer.vue';
import CatalogueRow from '~/components/mainPage/catalogueRow.vue';
import { useLoadSliderGames } from '~/composables/useLoadSliderGames';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsGames } from '~/stores/softswiss/games';
import { useStoreSsGamesFavourite } from '~/stores/softswiss/gamesFavourite';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreCataloguePage } from '~/stores/strapi/cataloguePage';
import type { IGamesRequestOptions, ISsGameMainPage, ISsGame } from '~/types/softswiss/ssGames';

const props = defineProps({
  gtmAction: {
    type: String,
    default: '',
  },
  classForContainer: {
    type: String,
    default: '',
  },
  suggestionCollection: {
    type: Object as PropType<ISsGameMainPage>,
    default: null,
  },
});
defineEmits<{
  (e: 'addToFavorite', value: number): void;
  (e: 'removeFromFavorite', value: number): void;
  (e: 'setCountCatalogueItem', value: number): void;
}>();

const router = useRouter();
const route = useRoute();

const storeSsGamesFavourite = useStoreSsGamesFavourite();
const storeSsGames = useStoreSsGames();
const storeSsPlayer = useStoreSsPlayer();
const storeCataloguePage = useStoreCataloguePage();

const { loadCollectionGames } = useLoadSliderGames();

const activeTab = ref<'favorites' | 'recent'>('favorites');
const favoriteGames = ref<ISsGame[]>([]);
const recentGames = ref<ISsGame[]>([]);
const suggestionGames = ref<ISsGame[] | null>(null);
const isGamesLoading = ref(true);

const isActiveTabHasGames = computed(() => {
  return activeTab.value === 'favorites' ? favoriteGames.value.length : recentGames.value.length;
});

const favoriteGamesIds = computed(() => storeSsGamesFavourite.gameIds);

const loadFavoriteGames = async () => {
  if (!storeSsPlayer.isPlayerAuthorised || !storeSsGamesFavourite.gameIds.length) {
    favoriteGames.value = [];
    if (storeSsPlayer.isPlayerDataLoadingFirstInitOnce) {
      isGamesLoading.value = false;
    }
    return;
  }
  isGamesLoading.value = true;
  const params: IGamesRequestOptions = {
    favoritgames: storeSsGamesFavourite.gameIds,
    includerestricted: true,
  };

  const favGamesWithMeta = await storeSsGames.loadGames(params);
  favoriteGames.value = favGamesWithMeta.data;
  isGamesLoading.value = false;
};
const loadRecentGames = () => {
  if (!storeSsPlayer.isPlayerAuthorised) {
    recentGames.value = [];
    if (storeSsPlayer.isPlayerDataLoadingFirstInitOnce) {
      isGamesLoading.value = false;
    }

    return;
  }
  isGamesLoading.value = true;
  storeSsGames.getGamesFromLocalStorage();
  recentGames.value = storeSsGames.recentlyPlayedGames;
  isGamesLoading.value = false;
};

const loadSuggestGames = async () => {
  suggestionGames.value = await loadCollectionGames(props.suggestionCollection.id);
};

const goToMainIfFavouritesAndNotAuth = () => {
  return router.push({ path: useStoreRoot().localePath('/') });
};

const setActiveTab = (tab: 'favorites' | 'recent') => {
  activeTab.value = tab;
};

watch(
  () => storeSsPlayer.isPlayerAuthorised,
  async (val) => {
    if (val) {
      if (activeTab.value === 'recent') {
        loadRecentGames();
      } else {
        await storeSsGamesFavourite.load();
        await loadFavoriteGames();
      }
    } else {
      goToMainIfFavouritesAndNotAuth();
    }
  }
);
watch(
  () => storeSsGamesFavourite.gameIds,
  async () => await loadFavoriteGames(),
  { deep: true }
);
watch(
  () => activeTab.value,
  (val) => {
    if (val === 'recent') {
      router.push({
        path: route.fullPath,
        query: { last_played: 'true' },
      });

      if (!recentGames.value.length) {
        loadRecentGames();
      }
    } else {
      router.push({
        path: route.fullPath,
        query: { ...route.query, last_played: undefined },
      });
      if (!favoriteGames.value.length) {
        loadFavoriteGames();
      }
    }
  }
);

watch(
  () => route.query.last_played,
  (val) => {
    if (val === 'true') {
      activeTab.value = 'recent';
    } else if (val !== 'true' && activeTab.value === 'recent') {
      activeTab.value = 'favorites';
    }
  }
);

watch(
  () => storeSsPlayer.isPlayerDataLoadingFirstInitOnce,
  () => {
    if (!storeSsPlayer.isPlayerAuthorised) {
      goToMainIfFavouritesAndNotAuth();
    }
    if (storeSsPlayer.isPlayerAuthorised && activeTab.value === 'favorites') {
      loadFavoriteGames();
    }
  }
);

onMounted(async () => {
  if (route.query?.last_played === 'true') {
    activeTab.value = 'recent';
    loadRecentGames();
  } else {
    await storeSsGamesFavourite.load({ force: true });
    loadFavoriteGames();
  }
  loadSuggestGames();
});
</script>

<style lang="scss" module src="./myGamesCategory.scss"></style>
